/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { TsriServiceEndpointService } from "@tivocorp/data-domain-lib";
import type {
  TServicesParams,
  ServiceEndpoints
} from "@tivocorp/data-domain-lib";

import type { IBaseInteractor } from "../../interfaces/IBaseInteractor";
import { Log } from "../../logging/Log";
import { OperationalLogger } from "../../logging/OperationalLogger";
import { ServiceApiLoggingStateProvider } from "../../logging/ServiceApiLoggingStateProvider";
import { DeviceModel } from "../../device";
import { CommonHeaders } from "../../CommonHeaders";
import { ApplicationFeatureArea } from "../../enums";
// Concrete module, not the `auth` barrel: the barrel pulls in the refresh
// strategy, which imports the interactor barrel, which imports this file.
import { TokenManager } from "../../auth/TokenManager";
import type { TAuthContext } from "../../auth/TokenManager";
import { AuthRejectionArbiter } from "../../auth/AuthRejectionArbiter";
import { getTokenGeneration } from "../../auth/tokenGeneration";
import { ErrorCodeTypes } from "../../enums/ErrorCodeTypes";
import type { LogDetails } from "../../types";
import { TSRI_BASE_URL, TSRI_ID } from "../../../config";
import { TsriEndpointResolver } from "../../tsri";
import { NetworkGuard } from "./NetworkGuard";
import type { TNetworkOptions } from "../../types/TNetworkOptions";
import { transformBaseUrl } from "../../../devtools/mockOverride";

/** Event name shared with the Android/Frumos cloudcoreTraffic operational schema. */
const CLOUDCORE_TRAFFIC_EVENT: string = "cloudcoreTraffic";

/**
 * Builds the error thrown for an offline/unreachable call, tagged with
 * `errorCodeType` so {@link ErrorCodeUtil} (via `AxiosErrorAdapter`) can
 * resolve it to a network sub-error code instead of falling through to "no
 * classification found" — this is the one signal available at this layer,
 * since the error is synthesized here rather than thrown by axios.
 */
function buildNetworkUnavailableError(): Error {
  const error: Error & { errorCodeType?: ErrorCodeTypes } = new Error(
    "Network unavailable"
  );
  error.errorCodeType = ErrorCodeTypes.CONNECTION_FAILED;
  return error;
}

/** EndpointUrl truncation length per the serviceAPILogging field spec. */
const ENDPOINT_URL_MAX_LENGTH: number = 200;

/**
 * Abstract base class for post-sign-in interactors.
 *
 * Encapsulates common operations shared across multiple interactor
 * implementations:
 * - TSRI service-endpoint resolution (with auto-refresh on cache miss)
 * - Authenticated TServicesParams construction (accessToken + bodyId
 *   from DeviceModel)
 * - Network-aware API guarding via NetworkGuard singleton (abort on
 *   offline, block when unavailable, optional retry on reconnect)
 *
 * Subclasses inherit these utilities and focus on domain-specific
 * facade methods only.
 */
export abstract class BaseInteractor implements IBaseInteractor {
  protected static readonly BASE_TAG: string = "BaseInteractor";

  /**
   * Builds TServicesParams for the TSRI service-endpoint catalog.
   * Uses LOGIN feature area since TSRI calls are part of infrastructure.
   */
  protected async buildTsriServicesParams(): Promise<TServicesParams> {
    const common = new CommonHeaders(ApplicationFeatureArea.LOGIN);
    return common.toServicesParams({
      basePath: TSRI_BASE_URL,
      bodyId: await DeviceModel.getTiVoSettings().getBodyId(),
      xAmznRequestId: DeviceModel.getDeviceUuid() || ""
    });
  }

  /**
   * Builds an authenticated TServicesParams by resolving the service
   * endpoint via TSRI and the access token via TokenManager.
   *
   * The token and the profile ID come from a single
   * {@link TokenManager.getAuthContext} call so the `Authorization` and
   * `profileId` headers always describe the same context — reading them from
   * separate sources let them disagree across a profile switch.
   *
   * @param serviceName - TSRI catalog key to resolve as basePath
   * @param featureArea - ApplicationFeatureArea for the request context
   * @returns Fully populated TServicesParams
   * @throws UnauthenticatedError | UnauthorisedError - When no valid token can
   *         be produced. Callers reach this from inside `executeGuarded`, so
   *         the failure is reported through the usual guard path.
   */
  protected async buildServicesParams(
    serviceName: string,
    featureArea: ApplicationFeatureArea
  ): Promise<TServicesParams> {
    const authContext: TAuthContext = await TokenManager.getAuthContext();
    const bodyId: string = await DeviceModel.getTiVoSettings().getBodyId();
    const common = new CommonHeaders(featureArea);
    return common.toServicesParams({
      basePath: transformBaseUrl(
        await this.getServiceEndpointByServiceName(serviceName)
      ),
      bodyId: bodyId,
      accessToken: authContext.accessToken,
      profileId:
        authContext.profileId === null ? undefined : authContext.profileId,
      timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
      xAmznRequestId: DeviceModel.getDeviceUuid()
    });
  }

  /**
   * Executes an API call through the NetworkGuard layer, recovering once from
   * a rejected session.
   *
   * A 401 or 403 from a CloudCore API is not taken at its word: it may be the
   * service's own refusal rather than a session problem. It is handed to
   * {@link AuthRejectionArbiter}, which asks `GET /secure` — 401 there means
   * the domain token is invalid (refresh), 403 means the device is not
   * provisioned (DPS), 200 means the rejection was the service's own. After a
   * recovery the call is replayed exactly once; a second rejection
   * propagates. Any other failure propagates untouched.
   *
   * The replay re-runs `fn` from the top, and `fn` resolves its token through
   * {@link buildServicesParams} — so the replay necessarily carries the new
   * token rather than the rejected one.
   *
   * @typeParam T - The return type
   * @param methodName - Used for logging/call-id
   * @param fn - The API invocation receiving guarded AbortSignal
   * @param options - Network options (signal, retryOnReconnect, etc.)
   * @returns The result of fn
   */
  protected async executeGuarded<T>(
    methodName: string,
    fn: (signal: AbortSignal) => Promise<T>,
    options?: TNetworkOptions
  ): Promise<T> {
    return this.executeWithAuthRecovery<T>(methodName, fn, options, true);
  }

  /**
   * {@link executeGuarded}'s body.
   *
   * @param mayReplayStale - Whether a rejection that arrived after the token
   *        changed may be replayed as stale. The token changes under a call
   *        either through a concurrent recovery or through the call's own
   *        token lookup refreshing an expired token; the snapshot cannot tell
   *        which. So the stale replay is itself arbitrated: if the new token is
   *        rejected too, `/secure` decides as for any rejection.
   */
  private async executeWithAuthRecovery<T>(
    methodName: string,
    fn: (signal: AbortSignal) => Promise<T>,
    options: TNetworkOptions | undefined,
    mayReplayStale: boolean
  ): Promise<T> {
    const TAG: string = this.getClassTag();
    const generationAtStart: number = getTokenGeneration();
    try {
      return await this.executeGuardedOnce<T>(methodName, fn, options);
    } catch (error: unknown) {
      const callerSignal: AbortSignal | undefined =
        options !== undefined && options !== null ? options.signal : undefined;
      const isAborted: boolean =
        callerSignal !== undefined && callerSignal.aborted;
      const skipAuthRecovery: boolean =
        options !== undefined &&
        options !== null &&
        options.skipAuthRecovery === true;
      if (
        isAborted ||
        skipAuthRecovery ||
        !AuthRejectionArbiter.isAuthRejection(error)
      ) {
        throw error;
      }
      if (mayReplayStale && getTokenGeneration() !== generationAtStart) {
        Log.d(
          TAG,
          methodName + "() — rejected as the token changed, replaying"
        );
        return this.executeWithAuthRecovery<T>(methodName, fn, options, false);
      }
      const shouldReplay: boolean = await AuthRejectionArbiter.resolve(
        methodName,
        error,
        generationAtStart
      );
      if (!shouldReplay) {
        throw error;
      }
      Log.w(TAG, methodName + "() — session recovered, replaying once");
      return this.executeGuardedOnce<T>(methodName, fn, options);
    }
  }

  /**
   * One pass of the guarded execution — the network gating, abort wiring and
   * reachability classification, without the credential retry above.
   *
   * Scenarios handled:
   * 1. Network down + retryOnReconnect disabled → throws immediately.
   * 2. Network down + retryOnReconnect enabled → queues for replay, throws.
   * 3. Network up → executes; if internet drops mid-flight the guard
   *    aborts the controller and queues retryable calls automatically.
   * 4. Caller signal abort → treated as intentional cancellation, logged
   *    at debug level, never retried, re-thrown as-is.
   *
   * @throws Error("Network unavailable") - When offline or reachability error detected
   * @throws Error from fn - Any error thrown by the API call
   */
  private async executeGuardedOnce<T>(
    methodName: string,
    fn: (signal: AbortSignal) => Promise<T>,
    options?: TNetworkOptions
  ): Promise<T> {
    const TAG: string = this.getClassTag();
    const guard: NetworkGuard = NetworkGuard.getInstance();

    const retryOnReconnect: boolean = this.shouldRetryOnReconnect(options);
    const bypassNavCancellation: boolean =
      this.shouldBypassNavCancellation(options);

    const buildReplayFn = (): (() => Promise<T>) => {
      return (): Promise<T> => this.executeGuarded<T>(methodName, fn, options);
    };

    // Pre-flight check: throws if offline
    this.handlePreCallNetworkGate(
      TAG,
      methodName,
      guard,
      retryOnReconnect,
      bypassNavCancellation,
      buildReplayFn
    );

    const callId: string = guard.generateCallId(methodName);
    const controller: AbortController = new AbortController();
    const callerSignal: AbortSignal | undefined =
      options !== undefined && options !== null ? options.signal : undefined;

    const abortHandler: (() => void) | null = this.setupCallerSignalLink(
      callerSignal,
      controller
    );

    guard.registerCall(
      callId,
      controller,
      retryOnReconnect ? buildReplayFn() : null,
      bypassNavCancellation
    );

    // Evaluated once so a single call always emits either both or neither
    // of its request/response events, even if the window edge is crossed
    // mid-flight.
    const serviceApiLoggingActive: boolean =
      ServiceApiLoggingStateProvider.isServiceApiLoggingActive();
    if (serviceApiLoggingActive) {
      BaseInteractor.emitCloudcoreTraffic("ReqToServer", callId, methodName);
    }
    const startTimeMs: number = Date.now();

    try {
      const result: T = await fn(controller.signal);
      guard.unregisterCall(callId);
      guard.hintInternetAvailability(true);
      if (serviceApiLoggingActive) {
        BaseInteractor.emitCloudcoreTraffic(
          "RespFromServer",
          callId,
          methodName,
          Date.now() - startTimeMs
        );
      }
      return result;
    } catch (error: unknown) {
      guard.unregisterCall(callId);
      if (serviceApiLoggingActive) {
        BaseInteractor.emitCloudcoreTraffic(
          "RespFromServer",
          callId,
          methodName,
          Date.now() - startTimeMs,
          error
        );
      }
      this.handleExecutionError(
        TAG,
        methodName,
        error,
        callerSignal,
        controller,
        guard
      );
      throw error;
    } finally {
      this.cleanupAbortHandler(abortHandler, callerSignal);
    }
  }

  /**
   * Emits a `cloudcoreTraffic` operational event for one leg (request or
   * response) of an outbound CloudCore call. Absent/zero fields are
   * omitted rather than sent as empty values. Bodies are never read.
   *
   * Field coverage is limited by what is visible at this call site (the
   * interactor layer): the resolved endpoint URL, HTTP method and
   * response headers are not available here because the underlying
   * service repositories only surface `response.data`. `Operation` uses
   * the interactor method name as the closest available stand-in for the
   * API operation name.
   */
  private static emitCloudcoreTraffic(
    direction: "ReqToServer" | "RespFromServer",
    id: string,
    operation: string,
    respDurationMs?: number,
    error?: unknown
  ): void {
    const details: LogDetails = {
      Time: new Date().toISOString(),
      Direction: direction,
      Id: id,
      Operation: operation.slice(0, ENDPOINT_URL_MAX_LENGTH)
    };

    if (respDurationMs !== undefined) {
      details.RespDuration = String(respDurationMs);
    }

    const httpStatus: string | null = BaseInteractor.extractHttpStatus(error);
    if (httpStatus !== null) {
      details.HttpStatus = httpStatus;
    }

    OperationalLogger.logEvent(CLOUDCORE_TRAFFIC_EVENT, details);
  }

  /** Extracts an axios-style `error.response.status` without using `any`. */
  private static extractHttpStatus(error: unknown): string | null {
    if (error === null || error === undefined || typeof error !== "object") {
      return null;
    }
    if (!("response" in error)) {
      return null;
    }

    const response: unknown = (error as { response?: unknown }).response;
    if (
      response === null ||
      response === undefined ||
      typeof response !== "object"
    ) {
      return null;
    }
    if (!("status" in response)) {
      return null;
    }

    const status: unknown = (response as { status?: unknown }).status;
    return typeof status === "number" ? String(status) : null;
  }

  private getClassTag(): string {
    const classTag = (this.constructor as { TAG?: string }).TAG;
    return classTag !== undefined && classTag !== null
      ? String(classTag)
      : BaseInteractor.BASE_TAG;
  }

  private shouldRetryOnReconnect(options?: TNetworkOptions): boolean {
    return (
      options !== undefined &&
      options !== null &&
      options.retryOnReconnect === true
    );
  }

  private shouldBypassNavCancellation(options?: TNetworkOptions): boolean {
    return (
      options !== undefined &&
      options !== null &&
      options.bypassNavigationCancellation === true
    );
  }

  private handlePreCallNetworkGate<T>(
    TAG: string,
    methodName: string,
    guard: NetworkGuard,
    retryOnReconnect: boolean,
    bypassNavCancellation: boolean,
    buildReplayFn: () => () => Promise<T>
  ): void {
    if (!guard.isInternetAvailable()) {
      if (retryOnReconnect) {
        guard.enqueueForRetry(buildReplayFn(), bypassNavCancellation);
        Log.w(
          TAG,
          methodName + "() — network down, queued for replay on reconnect"
        );
      }
      throw buildNetworkUnavailableError();
    }
  }

  private setupCallerSignalLink(
    callerSignal: AbortSignal | undefined,
    controller: AbortController
  ): (() => void) | null {
    if (callerSignal === undefined) {
      return null;
    }

    if (callerSignal.aborted) {
      controller.abort();
      return null;
    }

    const abortHandler = (): void => {
      controller.abort();
    };
    callerSignal.addEventListener("abort", abortHandler, { once: true });
    return abortHandler;
  }

  private handleExecutionError(
    TAG: string,
    methodName: string,
    error: unknown,
    callerSignal: AbortSignal | undefined,
    controller: AbortController,
    guard: NetworkGuard
  ): void {
    // Caller-initiated abort: log and return
    if (callerSignal !== undefined && callerSignal.aborted) {
      Log.d(TAG, methodName + "() — cancelled by caller signal");
      return;
    }

    // Navigation-initiated abort (cancelAllForNavigation): the guard
    // aborted the controller but the network is fine. Do NOT hint
    // internet down — just log and re-throw the original error.
    if (controller.signal.aborted && !NetworkGuard.isReachabilityError(error)) {
      Log.d(
        TAG,
        methodName + "() — cancelled by navigation, not a network error"
      );
      return;
    }

    // Genuine reachability error: hint internet down
    if (NetworkGuard.isReachabilityError(error)) {
      Log.w(
        TAG,
        methodName + "() — reachability error detected, hinting internet down"
      );
      guard.hintInternetAvailability(false);
      throw buildNetworkUnavailableError();
    }

    guard.hintInternetAvailability(true);
    Log.e(TAG, methodName + "()", error);
  }

  private cleanupAbortHandler(
    abortHandler: (() => void) | null,
    callerSignal: AbortSignal | undefined
  ): void {
    if (abortHandler !== null && callerSignal !== undefined) {
      callerSignal.removeEventListener("abort", abortHandler);
    }
  }

  public async fetchServiceEndpoints(
    signal?: AbortSignal
  ): Promise<ServiceEndpoints> {
    try {
      Log.d(BaseInteractor.BASE_TAG, "fetchServiceEndpoints() called");
      const servicesParams = await this.buildTsriServicesParams();
      const service: TsriServiceEndpointService =
        new TsriServiceEndpointService(servicesParams);
      return await service.fetchServiceEndpoints(
        {
          tsriId: TSRI_ID,
          originRequestId: servicesParams.xAmznRequestId || ""
        },
        signal
      );
    } catch (error: unknown) {
      Log.e(BaseInteractor.BASE_TAG, "fetchServiceEndpoints()", error);
      throw error;
    }
  }

  /**
   * Resolves a service's basePath from the TSRI endpoint cache.
   *
   * Delegates to `TsriEndpointResolver`, which on a cache miss re-fetches the
   * catalog on the INTERACTIVE policy — raising the alert overlay first when
   * the service is in `CRITICAL_TSRI_SERVICES`, silently otherwise — and throws
   * once the budget is exhausted.
   *
   * This replaced a fetch-once-then-throw: a single unconditional re-fetch with
   * no backoff, no overlay, and no second chance if the catalog came back
   * without the service again.
   */
  public async getServiceEndpointByServiceName(
    serviceName: string
  ): Promise<string> {
    Log.d(BaseInteractor.BASE_TAG, "getServiceEndpointByServiceName() called");
    return TsriEndpointResolver.getInstance().resolve(serviceName);
  }
}
